import React from 'react';
import classnames from 'classnames';
import { breakpoints } from '../../foundations/breakpoints';
import {
  DynamicMediaPicture,
  DynamicMediaPictureProps,
  MultiDynamicMediaImageConfigs,
  Text,
  TextButtonProps,
  TextLinkProps,
  TextButton,
  TextLink,
  IconButton
} from '../index';
import styles from './banner.module.scss';

export type BannerProps = {
  image?: Omit<DynamicMediaPictureProps, 'imageConfig' | 'className' | 'hasDarkeningLayer'>;
  className?: string;
  headline: string;
  text: string;
  cta?:
    | Omit<TextButtonProps, 'variant' | 'size' | 'iconPosition'>
    | Omit<TextLinkProps, 'variant' | 'size' | 'iconPosition'>;
  closeButton: { onClick: (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void; ariaLabel: string };
  'aria-hidden'?: boolean;
  role?: React.AriaRole;
};

const imageConfig: MultiDynamicMediaImageConfigs = [
  {
    aspectRatio: '4-3',
    sizes: '25vw',
    media: `(max-width: ${breakpoints.m}px)`
  },
  {
    aspectRatio: '4-3',
    sizes: '10vw',
    media: `(min-width: ${breakpoints.m}px)`
  }
];

export const isBannerTextLink = (
  linkProps: unknown
): linkProps is Omit<TextLinkProps, 'variant' | 'size' | 'iconPosition'> =>
  typeof (linkProps as TextLinkProps)?.href === 'string' && (linkProps as TextLinkProps)?.linkText !== undefined;

export const Banner: React.FC<BannerProps> = ({
  className,
  image,
  headline,
  text,
  cta,
  closeButton,
  role,
  'aria-hidden': ariaHidden
}) => {
  const classes = classnames(styles.banner, className, 'setting--inverted');
  return (
    <div className={classes} role={role} aria-hidden={ariaHidden} data-testid="banner-component">
      <div className={styles.content}>
        {image && <DynamicMediaPicture {...image} imageConfigs={imageConfig} className={styles.image} />}
        <div className={styles.textContainer}>
          <Text variant="label-100" className={styles.headline}>
            {headline}
          </Text>
          <Text className={styles.text}>{text}</Text>
          {cta &&
            (isBannerTextLink(cta) ? (
              <TextLink {...cta} variant="button-primary" size="200" iconPosition="right" />
            ) : (
              <TextButton {...cta} variant="primary" size="200" iconPosition="right" />
            ))}
        </div>
      </div>
      <IconButton
        size="200"
        iconName="close-without-circle--small"
        variant="ghost"
        onClick={closeButton.onClick}
        ariaLabel={closeButton.ariaLabel}
      />
    </div>
  );
};
